import * as R from 'ramda';

import { WeeklyScheduleInputEntry } from './types';
import { WeeklySchedule } from '../types';
import { DAYS_OF_WEEK } from '../values';

// `RawEntry` and `RawTimePair` are intermediate data types used in processing
// OpeningTimes. RawEntry maps directly to WeeklyScheduleEntry, but it is
// simpler to work with when doing transforms.
type RawTimePair = { start: number; end: number };
export type RawEntry = {
  weekdayIndices: number[];
  timePairs: RawTimePair[];
};

const MINUTE_DURATION_MS = 60 * 1000;
const DAY_DURATION_MS = 24 * 60 * MINUTE_DURATION_MS;
const WEEK_DURATION_MS = 7 * DAY_DURATION_MS;

// Very inefficient set comparison function. Do not use on large sets!
const setsEqual = <T>(a: T[], b: T[]) =>
  a.length === b.length && a.every(itemA => b.some(itemB => R.equals(itemA, itemB)));

const timePairDuration = (timePair: RawTimePair) => {
  return (timePair.end > timePair.start ? timePair.end : timePair.end + DAY_DURATION_MS) - timePair.start;
};

const findIndicesOfContiguousTimePairs = (timePairs: RawTimePair[]): [number, number] | undefined => {
  for (let i = 0; i < timePairs.length; i += 1) {
    for (let j = 0; j < timePairs.length; j += 1) {
      if (i !== j && timePairs[i].end === timePairs[j].start) {
        return timePairs[i].start < timePairs[j].start ? [i, j] : [j, i];
      }
    }
  }
  return undefined;
};

const mergeContiguousTimePairs = (timePairs: RawTimePair[]): RawTimePair[] => {
  const indicesOfContiguousTimePairs = findIndicesOfContiguousTimePairs(timePairs);

  if (!indicesOfContiguousTimePairs) {
    return timePairs;
  }

  const [timePairAIndex, timePairBIndex] = indicesOfContiguousTimePairs;

  const timePairA = timePairs[timePairAIndex];
  const timePairB = timePairs[timePairBIndex];

  return mergeContiguousTimePairs(
    R.remove(timePairBIndex, 1, R.update(timePairAIndex, { start: timePairA.start, end: timePairB.end }, timePairs)),
  );
};

export const splitWeeklyTimePairAtMidnights = (timePair: {
  start: number;
  end: number;
}): { start: number; end: number }[] => {
  const duration = (timePair.end > timePair.start ? timePair.end : timePair.end + WEEK_DURATION_MS) - timePair.start;

  if (duration <= DAY_DURATION_MS) {
    return [timePair];
  }

  const splitAt =
    (timePair.start % DAY_DURATION_MS === 0
      ? timePair.start + DAY_DURATION_MS
      : Math.ceil(timePair.start / DAY_DURATION_MS) * DAY_DURATION_MS) % WEEK_DURATION_MS;

  return [
    { start: timePair.start, end: splitAt },
    ...splitWeeklyTimePairAtMidnights({ start: splitAt, end: timePair.end }),
  ];
};

// Produce a raw intermediate data structure where each time span has its own entry
const openingTimesToRawEntries = (times: WeeklySchedule): RawEntry[] => {
  const flattenedEvents = DAYS_OF_WEEK.flatMap((weekday, weekdayIndex) =>
    times[weekday].map(event => ({
      type: event.type,
      timeSinceStartOfWeek: event.value.$date + weekdayIndex * DAY_DURATION_MS,
    })),
  );

  const openingTimes = flattenedEvents
    .filter(closeEvent => closeEvent.type === 'open')
    .map(openEvent => openEvent.timeSinceStartOfWeek);
  const closingTimes = flattenedEvents
    .filter(closeEvent => closeEvent.type === 'close')
    .map(closeEvent => closeEvent.timeSinceStartOfWeek);
  const weeklyTimePairs = openingTimes
    .map(openingTime => {
      const nextClosingTimes = closingTimes
        .map(closingTime => (closingTime > openingTime ? closingTime : closingTime + WEEK_DURATION_MS))
        .sort((a, b) => a - b);
      const [nextClosingTime] = nextClosingTimes;
      return {
        start: openingTime,
        end: nextClosingTime % WEEK_DURATION_MS,
      };
    })
    .flatMap(splitWeeklyTimePairAtMidnights);

  return weeklyTimePairs.map(weeklyTimePair => {
    const weekdayIndex = Math.floor(weeklyTimePair.start / DAY_DURATION_MS);
    return {
      weekdayIndices: [weekdayIndex],
      timePairs: [
        {
          start: weeklyTimePair.start - weekdayIndex * DAY_DURATION_MS,
          end: weeklyTimePair.end % DAY_DURATION_MS,
        },
      ],
    };
  });
};

export const groupByWeekday = (entries: RawEntry[]) => {
  return entries.reduce<RawEntry[]>((groupedEntries, entry) => {
    const indexOfEntryWithSameWeekdays = groupedEntries.findIndex(groupedEntry =>
      setsEqual(groupedEntry.weekdayIndices, entry.weekdayIndices),
    );

    if (indexOfEntryWithSameWeekdays === -1) {
      return [...groupedEntries, entry];
    }

    const entryWithSameWeekdays = groupedEntries[indexOfEntryWithSameWeekdays];
    return R.update(
      indexOfEntryWithSameWeekdays,
      {
        ...entryWithSameWeekdays,
        timePairs: [...entryWithSameWeekdays.timePairs, ...entry.timePairs],
      },
      groupedEntries,
    );
  }, []);
};

export const mergeContiguousTimePairsWithinEntries = (entries: RawEntry[]) => {
  return entries.map(entry => ({
    ...entry,
    timePairs: mergeContiguousTimePairs(entry.timePairs),
  }));
};

export const groupByTimePairs = (entries: RawEntry[]) => {
  return entries.reduce<RawEntry[]>((groupedEntries, entry) => {
    const indexOfEntryWithSameTimePairs = groupedEntries.findIndex(groupedEntry =>
      setsEqual(groupedEntry.timePairs, entry.timePairs),
    );

    if (indexOfEntryWithSameTimePairs === -1) {
      return [...groupedEntries, entry];
    }

    const entryWithSameTimePairs = groupedEntries[indexOfEntryWithSameTimePairs];
    return R.update(
      indexOfEntryWithSameTimePairs,
      {
        ...entryWithSameTimePairs,
        weekdayIndices: R.uniq([...entryWithSameTimePairs.weekdayIndices, ...entry.weekdayIndices])
          // Sorting of weekday indices is only required for unit tests. Without it, comparing
          // values of `daysOfWeek` properties becomes awkward. Sorting of daysOfWeek for display
          // should be done in the view and be locale aware (Mon-Sun vs. Sun-Sat).
          .sort((a, b) => a - b),
      },
      groupedEntries,
    );
  }, []);
};

export const floorTimePairsToMinutes = (entries: RawEntry[]): RawEntry[] => {
  return entries
    .map(entry => ({
      ...entry,
      timePairs: entry.timePairs
        .map(timePair => {
          const floored = {
            start: Math.floor(timePair.start / MINUTE_DURATION_MS) * MINUTE_DURATION_MS,
            end: Math.floor(timePair.end / MINUTE_DURATION_MS) * MINUTE_DURATION_MS,
          };
          return timePairDuration(floored) === DAY_DURATION_MS && timePairDuration(timePair) < MINUTE_DURATION_MS
            ? null
            : floored;
        })
        .filter((timePair): timePair is RawTimePair => timePair !== null),
    }))
    .filter(entry => entry.timePairs.length > 0);
};

export const rawEntriesToWeeklyScheduleEntries = (rawEntries: RawEntry[]): WeeklyScheduleInputEntry[] => {
  return rawEntries.map(rawEntry => ({
    daysOfWeek: rawEntry.weekdayIndices.map(weekdayIndex => DAYS_OF_WEEK[weekdayIndex]),
    timePairs: rawEntry.timePairs.map(rawTimePair => ({
      startMinutes: rawTimePair.start / MINUTE_DURATION_MS,
      endMinutes: rawTimePair.end / MINUTE_DURATION_MS + (rawTimePair.end <= rawTimePair.start ? 24 * 60 : 0),
    })),
    open24Hours:
      rawEntry.timePairs.length === 1 &&
      rawEntry.timePairs[0].start === 0 &&
      timePairDuration(rawEntry.timePairs[0]) === DAY_DURATION_MS,
  }));
};

// Does not return daysOfWeek in any particular order. They are treated as a set and ordering
// for display should be done in the view and be locale aware (Mon-Sun vs. Sun-Sat).
const fromWeeklyScheduleToWeeklyScheduleInputEntries: (openingTimes: WeeklySchedule) => WeeklyScheduleInputEntry[] =
  R.pipe(
    openingTimesToRawEntries,
    groupByWeekday,
    mergeContiguousTimePairsWithinEntries,
    groupByTimePairs,
    floorTimePairsToMinutes,
    rawEntriesToWeeklyScheduleEntries,
    // TODO: Return entries in some sensible order. This should probably be locale aware (Mon-Sun vs. Sun-Sat).
  );

export default fromWeeklyScheduleToWeeklyScheduleInputEntries;
